// utils/eventUtils.js

/**
 * Format a number to 2 decimals (generic).
 * Example: fmt(1700) -> "1,700.00"
 */
export const fmt = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  return num.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/**
 * Format Firestore Timestamp or Date to locale string safely.
 */
export const fmtTs = (ts) => {
  try {
    return ts?.toDate?.()?.toLocaleString?.() || "—";
  } catch {
    return "—";
  }
};

/**
 * Firestore rejects undefined values.
 * Remove keys with undefined values from an object.
 */
export const cleanObject = (obj) =>
  Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined));

/**
 * Compute age from:
 *  - number (years)
 *  - Date
 *  - ISO string
 *  - Firestore Timestamp (with toDate())
 */
export const getAge = (value) => {
  if (value == null) return null;
  if (typeof value === "number" && Number.isFinite(value)) return value >= 0 ? value : null;
  try {
    const d =
      typeof value?.toDate === "function"
        ? value.toDate()
        : typeof value === "string"
        ? new Date(value)
        : value instanceof Date
        ? value
        : null;
    if (!d || Number.isNaN(d.getTime())) return null;
    const now = new Date();
    let age = now.getFullYear() - d.getFullYear();
    const m = now.getMonth() - d.getMonth();
    if (m < 0 || (m === 0 && now.getDate() < d.getDate())) age--;
    return age >= 0 ? age : null;
  } catch {
    return null;
  }
};

/**
 * Role presets used in Committee UI.
 */
export const ROLE_PRESETS = [
  { value: "incharge", label: "Incharge" },
  { value: "accountant", label: "Accountant" },
  { value: "host", label: "Host" },
  { value: "food-incharge", label: "Food Incharge" },
  { value: "other", label: "Other (custom)" },
];

/**
 * Normalize a committee record and drop undefined keys.
 */
export const normalizeCommitteeItem = (item) => {
  const result = {
    uid: item.uid || undefined,
    residentName:
      typeof item.residentName === "string" && item.residentName.trim()
        ? item.residentName.trim()
        : undefined,
    role: item.role || undefined,
    roleLabel: item.roleLabel || undefined,
    gender: item.gender || undefined,
    blockName: item.blockName || undefined,
    flatLabel: item.flatLabel || undefined,
  };
  return cleanObject(result);
};

/**
 * Calculate auto amount for a household.
 * adultFee * adults + childFee * (kids - freeKids)
 */
export const calculateAutoAmount = ({ adultFee, childFee, adults, kids, freeKids }) => {
  const chargedKids = Math.max(0, Number(kids || 0) - Number(freeKids || 0));
  const total =
    Number(adultFee || 0) * Number(adults || 0) +
    Number(childFee || 0) * chargedKids;
  return total;
};

/**
 * UI currency formatter with ₹ and Indian grouping.
 * Example: fmtInr(1700) -> "₹ 1,700.00"
 */
export const fmtInr = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  try {
    // Use locale + currency for consistent symbol and grouping
    return new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR" }).format(num);
  } catch {
    // Fallback in environments without Intl currency support
    return `₹ ${num.toLocaleString("en-IN", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }
};

/**
 * PDF-safe INR formatter (avoid ₹ glyph issues in jsPDF default font).
 * Example: fmtInrPdf(1700) -> "INR 1,700.00"
 *
 * If you want rupee symbol in PDF, you must embed a Unicode font.
 */
export const fmtInrPdf = (n) => {
  const num = Number(n);
  if (Number.isNaN(num)) return "—";
  const amount = num.toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `INR ${amount}`;
};
